import { sendUpload } from './upload-transport';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  openCommunicationSocket,
  type CommunicationConnection,
  type CommunicationEnvelope,
} from './realtime';
import type {
  Account,
  ConversationMessagePage,
  ConversationMessageView,
  Organization,
  Page,
  StorageFile,
} from '@yingbaba/types';

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public requestId: string,
    public status: number,
  ) {
    super(message);
  }
}
let csrf = '',
  organization = '',
  namespace = '';
export function configure(admin = false) {
  namespace = admin ? '/admin' : '';
  csrf = '';
  organization = '';
}
export function selectOrg(id: string) {
  organization = id;
  if (typeof window !== 'undefined') localStorage.setItem('yb.organization', id);
}
export function orgID() {
  return organization;
}
export function apiOrigin() {
  const configured = String(import.meta.env.VITE_API_ORIGIN || '').trim();
  if (configured) {
    const url = new URL(configured);
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      url.username ||
      url.password ||
      url.search ||
      url.hash ||
      !['', '/'].includes(url.pathname)
    )
      throw new Error('VITE_API_ORIGIN 必须是完整的 API 域名');
    return url.origin;
  }
  if (
    typeof window === 'undefined' ||
    ['localhost', '127.0.0.1'].includes(window.location.hostname)
  )
    return '';
  return 'https://api.yingbaba.com';
}
export function apiURL(path: string) {
  return `${apiOrigin()}/api/v1${path}`;
}
let directMode: boolean | undefined;
let modePromise: Promise<boolean> | undefined;
async function usesDirectStorage(force = false): Promise<boolean> {
  if (force) {
    directMode = undefined;
    modePromise = undefined;
  }
  if (directMode !== undefined) return directMode;
  if (!modePromise)
    modePromise = api<{ features: { upload_mode?: string } }>('/meta')
      .then((meta) => {
        directMode = meta.features.upload_mode === 'direct';
        return directMode;
      })
      .finally(() => {
        modePromise = undefined;
      });
  return modePromise;
}
export function captureUploadContext() {
  const scope = { organization, namespace, csrf };
  return () => {
    if (organization !== scope.organization || namespace !== scope.namespace || csrf !== scope.csrf)
      throw new Error('账号或组织已切换，请重新上传');
  };
}
interface UploadTicket {
  id: string;
  url: string;
  fields: Record<string, string>;
}
interface UploadStatus<T> {
  status: string;
  result?: T;
  message?: string;
  error_code?: string;
}
async function completeUpload<T>(
  base: string,
  id: string,
  current: () => void,
  signal?: AbortSignal | null,
) {
  for (let attempt = 0; ; attempt++) {
    current();
    try {
      return await api<UploadStatus<T>>(`${base}/${id}/complete`, { method: 'POST', signal });
    } catch (error) {
      if (
        signal?.aborted ||
        (error instanceof DOMException && error.name === 'AbortError') ||
        attempt >= 2 ||
        (error instanceof ApiError && error.status < 500)
      )
        throw error;
      // Retry the idempotent confirmation only, never the file POST to OSS.
      await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
    }
  }
}
export type UploadStage = 'preparing' | 'uploading' | 'confirming';
type UploadAttempt = {
  current: () => void;
  ticket?: UploadTicket;
  uploaded?: boolean;
  onStage?: (stage: UploadStage) => void;
  onProgress?: (percent: number) => void;
};
// A retry keeps the original confirmation ticket after bytes reach OSS. This
// also covers a lost final response: completion is idempotent on the server.
export function createUpload<T>(path: string, form: FormData) {
  const controller = new AbortController();
  const attempt: UploadAttempt = { current: captureUploadContext() };
  return {
    cancel: () => controller.abort(),
    async run(
      onStage?: (stage: UploadStage) => void,
      onProgress?: (percent: number) => void,
    ): Promise<T> {
      attempt.current();
      controller.signal.throwIfAborted();
      attempt.onStage = onStage;
      attempt.onProgress = onProgress;
      onStage?.('preparing');
      const direct = await usesDirectStorage();
      attempt.current();
      controller.signal.throwIfAborted();
      if (direct) return directUpload<T>(path, form, controller.signal, attempt);
      onStage?.('uploading');
      return api<T>(path, { method: 'POST', body: form, signal: controller.signal });
    },
  };
}
async function directUpload<T>(
  path: string,
  form: FormData,
  signal?: AbortSignal | null,
  attempt: UploadAttempt = { current: captureUploadContext() },
): Promise<T> {
  const current = attempt.current;
  const file = form.get('file') || form.get('thumbnail');
  if (!(file instanceof Blob)) throw new Error('请选择上传文件');
  const fields: Record<string, string> = {};
  for (const key of [
    'kind',
    'duration_seconds',
    'version_of',
    'expected_version',
    'version_note',
  ]) {
    const value = form.get(key);
    if (typeof value === 'string') fields[key] = value;
  }
  const name = String(
    form.get('file_name') || (file instanceof File ? file.name : 'thumbnail.jpg'),
  );
  const base = path.startsWith('/admin/') ? '/admin/storage/uploads' : '/storage/uploads';
  const target = path === '/admin/auth/account/avatar' ? '/account/avatar' : path.split('?')[0];
  current();
  attempt.onStage?.('preparing');
  if (!attempt.ticket) {
    attempt.ticket = await api<UploadTicket>(base, {
      method: 'POST',
      signal,
      body: JSON.stringify({ target, name, size: file.size, fields }),
    });
  }
  const ticket = attempt.ticket;
  current();
  if (!attempt.uploaded) {
    attempt.onStage?.('uploading');
    const body = new FormData();
    for (const [key, value] of Object.entries(ticket.fields)) body.append(key, value);
    body.append('file', file, name);
    const response = attempt.onProgress
      ? await sendUpload(ticket.url, body, signal, attempt.onProgress)
      : await fetch(ticket.url, { method: 'POST', body, credentials: 'omit', signal });
    if (!response.ok) {
      // No confirmation has been sent yet, so a fresh policy is safe (e.g. expiry).
      attempt.ticket = undefined;
      throw new Error('文件直传失败，请检查网络后重试');
    }
    attempt.uploaded = true;
  }
  current();
  attempt.onStage?.('confirming');
  let status: UploadStatus<T>;
  try {
    status = await completeUpload<T>(base, ticket.id, current, signal);
  } catch (error) {
    // Expiry is returned only for an uncommitted intent; a successful intent
    // always returns its existing result, even after the original deadline.
    if (error instanceof ApiError && error.code === 'UPLOAD_EXPIRED') {
      attempt.ticket = undefined;
      attempt.uploaded = false;
    }
    throw error;
  }
  current();
  if (status.status !== 'succeeded') {
    if (['failed', 'expired'].includes(status.status)) {
      attempt.ticket = undefined;
      attempt.uploaded = false;
    }
    throw new ApiError(
      status.message || '文件处理失败，请重新上传',
      status.error_code || 'UPLOAD_FAILED',
      '',
      400,
    );
  }
  const thumbnail = form.get('thumbnail');
  if (form.get('file') && thumbnail instanceof Blob) {
    const result = status.result as { id?: string };
    if (result?.id) {
      const extra = new FormData();
      extra.append('thumbnail', thumbnail, 'thumbnail.jpg');
      const thumbnailPath = path.startsWith('/customer-service/')
        ? `/customer-service/files/${result.id}/thumbnail`
        : `/communication-files/${result.id}/thumbnail`;
      // Customer-service administrator uploads do not expose a separate thumbnail endpoint.
      if (!path.startsWith('/admin/')) await directUpload(thumbnailPath, extra, signal);
    }
  }
  return status.result as T;
}
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const currentUpload = init.body instanceof FormData ? captureUploadContext() : null;
  if (init.body instanceof FormData) {
    const direct = await usesDirectStorage();
    currentUpload?.();
    if (direct) return directUpload<T>(path, init.body, init.signal);
  }
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData))
    headers.set('Content-Type', 'application/json');
  const method = (init.method || 'GET').toUpperCase();
  if (csrf && !['GET', 'HEAD', 'OPTIONS'].includes(method)) headers.set('X-CSRF-Token', csrf);
  if (
    organization &&
    !/^\/(?:admin\/)?auth\//.test(path) &&
    path !== '/meta' &&
    !path.startsWith('/marketplace/')
  )
    headers.set('X-Organization-ID', organization);
  const res = await fetch(apiURL(path), { ...init, headers, credentials: 'include' });
  let data;
  try {
    data = await res.json();
  } catch {
    throw new ApiError(
      '服务暂时无法连接，请稍后重试',
      'INVALID_RESPONSE',
      res.headers.get('X-Request-ID') || '',
      res.status,
    );
  }
  if (!res.ok && data.code === 'DIRECT_UPLOAD_REQUIRED' && init.body instanceof FormData) {
    if (await usesDirectStorage(true)) {
      currentUpload?.();
      return directUpload<T>(path, init.body, init.signal);
    }
  }
  if (!res.ok)
    throw new ApiError(data.message || '请求失败', data.code, data.request_id, res.status);
  return data.data as T;
}
export const post = <T>(path: string, data: unknown = {}) =>
  api<T>(path, { method: 'POST', body: JSON.stringify(data) });
export const remove = <T>(path: string) => api<T>(path, { method: 'DELETE' });
export const postIdempotent = <T>(
  path: string,
  data: unknown = {},
  key: string = crypto.randomUUID(),
) =>
  api<T>(path, {
    method: 'POST',
    headers: { 'Idempotency-Key': key },
    body: JSON.stringify(data),
  });
export interface Session {
  account: Account;
  organizations: (Organization & { role: string })[];
  csrf_token: string;
}
export function useSession() {
  return useQuery({
    queryKey: ['session', namespace],
    queryFn: async () => {
      try {
        const s = await api<Session>(`${namespace}/auth/session`);
        csrf = s.csrf_token;
        const saved = localStorage.getItem('yb.organization');
        selectOrg(s.organizations.find((o) => o.id === saved)?.id || s.organizations[0]?.id || '');
        return s;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
    enabled: typeof window !== 'undefined',
    staleTime: 60_000,
  });
}
export function useList<T>(path: string, enabled = true) {
  return useQuery({ queryKey: [path, organization], queryFn: () => api<Page<T>>(path), enabled });
}
export function useData<T>(path: string, enabled = true, interval?: number) {
  return useQuery({
    queryKey: [path, organization],
    queryFn: () => api<T>(path),
    enabled,
    refetchInterval: interval,
  });
}

export function useConversationMessages(path: string, enabled = true, limit = 100) {
  const latest = useData<ConversationMessagePage>(`${path}?limit=${limit}`, enabled);
  const scope = `${namespace}:${organization}:${path}`;
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const pending = useRef('');
  const [history, setHistory] = useState<{
    scope: string;
    items: ConversationMessageView[];
    hasMore: boolean;
  } | null>(null);
  const [loadingScope, setLoadingScope] = useState('');
  const [failure, setFailure] = useState<{ scope: string; error: unknown } | null>(null);
  const older = history?.scope === scope ? history.items : [];
  const loadingOlder = loadingScope === scope;

  useEffect(() => {
    activeScope.current = scope;
    return () => {
      activeScope.current = '';
    };
  }, [scope]);

  const items = useMemo(() => {
    const all = [...older, ...(latest.data?.items || [])];
    const seen = new Set<string>();
    return all.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [older, latest.data?.items]);

  const hasMore = history?.scope === scope ? history.hasMore : (latest.data?.has_more ?? false);
  const loadOlder = async () => {
    const first = items[0];
    if (!enabled || !first || pending.current === scope || !hasMore) return;
    pending.current = scope;
    setLoadingScope(scope);
    setFailure(null);
    try {
      const page = await api<ConversationMessagePage>(
        `${path}?before_seq=${encodeURIComponent(String(first.seq))}&limit=${limit}`,
      );
      if (activeScope.current !== scope) return;
      setHistory((current) => ({
        scope,
        items: [...(page.items || []), ...(current?.scope === scope ? current.items : [])],
        hasMore: page.has_more,
      }));
    } catch (error) {
      if (activeScope.current === scope) setFailure({ scope, error });
    } finally {
      if (pending.current === scope) pending.current = '';
      setLoadingScope((value) => (value === scope ? '' : value));
    }
  };

  return {
    latest,
    items,
    hasMore,
    loadingOlder,
    loadOlder,
    error: failure?.scope === scope ? failure.error : null,
  };
}

// Keep drafts and pending actions with their recipient. Late responses must not
// clear another conversation's draft or send an uploaded file under a new account.
export function useConversationComposer({
  conversationID,
  accountID,
  basePath = '',
  fileDeletePath = '/communication-files',
  listPath = '/conversations',
  canSend = true,
}: {
  conversationID: string;
  accountID: string;
  basePath?: string;
  fileDeletePath?: string;
  listPath?: string;
  canSend?: boolean;
}) {
  const qc = useQueryClient();
  const scope = `${namespace}:${accountID}:${organization}:${basePath}:${conversationID}`;
  const active = useRef({ scope, canSend });
  active.current = { scope, canSend };
  const operations = useRef(new Set<string>());
  const attempts = useRef(new Map<string, { text: string; id: string }>());
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [working, setWorking] = useState<Record<string, 'text' | 'file'>>({});
  const [failure, setFailure] = useState<{ scope: string; error: unknown } | null>(null);
  const text = drafts[scope] || '';
  const path = `${basePath}/conversations/${conversationID}`;
  useEffect(() => {
    active.current = { scope, canSend };
    return () => {
      active.current = { scope: '', canSend: false };
    };
  }, [scope, canSend]);

  const setText = (value: string) => setDrafts((current) => ({ ...current, [scope]: value }));
  const isCurrent = () => active.current.scope === scope && active.current.canSend;
  const begin = (kind: 'text' | 'file') => {
    if (!conversationID || !canSend || operations.current.has(scope)) return false;
    operations.current.add(scope);
    setWorking((current) => ({ ...current, [scope]: kind }));
    setFailure(null);
    return true;
  };
  const finish = () => {
    operations.current.delete(scope);
    setWorking((current) => {
      const next = { ...current };
      delete next[scope];
      return next;
    });
  };
  const refresh = () =>
    qc.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === listPath ||
        (typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith(`${path}/messages`)),
    });
  const sendText = async () => {
    const content = text.trim();
    if (!content || !begin('text')) return false;
    const previous = attempts.current.get(scope);
    const attempt =
      previous?.text === content ? previous : { text: content, id: crypto.randomUUID() };
    attempts.current.set(scope, attempt);
    try {
      await post(`${path}/messages`, { type: 'text', content, client_message_id: attempt.id });
      attempts.current.delete(scope);
      setDrafts((current) => (current[scope] === text ? { ...current, [scope]: '' } : current));
      if (isCurrent()) await refresh();
      return isCurrent();
    } catch (error) {
      if (isCurrent()) setFailure({ scope, error });
      return false;
    } finally {
      finish();
    }
  };
  const attach = async (file?: File) => {
    if (!file || !begin('file')) return false;
    let stored: StorageFile | null = null;
    try {
      stored = await uploadCommunicationFile<StorageFile>(`${path}/files`, file);
      if (!isCurrent()) return false;
      await post(`${path}/messages`, {
        type: 'file',
        content: '',
        file_ids: [stored.id],
        client_message_id: crypto.randomUUID(),
      });
      if (isCurrent()) await refresh();
      return isCurrent();
    } catch (error) {
      if (stored && isCurrent())
        await remove(`${fileDeletePath}/${stored.id}`).catch(() => undefined);
      if (isCurrent()) setFailure({ scope, error });
      return false;
    } finally {
      finish();
    }
  };
  return {
    text,
    setText,
    sendText,
    attach,
    sending: working[scope] === 'text',
    uploading: working[scope] === 'file',
    error: failure?.scope === scope ? failure.error : null,
  };
}
export function useAction<T = unknown>(path: string, onSuccess?: (data: T) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => post<T>(path, data),
    onSuccess: async (data) => {
      await qc.invalidateQueries();
      onSuccess?.(data);
    },
  });
}
export async function login(email: string, password: string, mfaCode = '') {
  const data = await post<{ csrf_token: string }>(`${namespace}/auth/login`, {
    email,
    password,
    mfa_code: mfaCode,
  });
  csrf = data.csrf_token;
  return data;
}
export async function weChatWebsiteLogin(path: 'bind' | 'complete', data: unknown) {
  const result = await post<{ csrf_token: string }>(`/auth/wechat/${path}`, data);
  csrf = result.csrf_token;
  return result;
}
export async function logout() {
  await post(`${namespace}/auth/logout`);
  csrf = '';
  organization = '';
}
export async function downloadFile(id: string, name: string) {
  return downloadPrivateFile(`/files/${id}/download`, name);
}

export function communicationSocketURL(withOrganization = false) {
  if (typeof window === 'undefined') return '';
  const origin = new URL(apiOrigin() || window.location.origin);
  origin.protocol = origin.protocol === 'https:' ? 'wss:' : 'ws:';
  const organizationQuery =
    withOrganization && organization ? `?organization_id=${encodeURIComponent(organization)}` : '';
  return `${origin.origin}/api/v1${namespace}/communication/ws${organizationQuery}`;
}

export function useCommunicationRealtime({
  accountID,
  enabled = true,
  withOrganization = false,
  onEvent,
}: {
  accountID: string;
  enabled?: boolean;
  withOrganization?: boolean;
  onEvent: (event: CommunicationEnvelope) => void;
}) {
  const qc = useQueryClient();
  const url = communicationSocketURL(withOrganization);
  const authNamespace = namespace;
  const organizationID = withOrganization ? organization : '';
  const scope = `${accountID}:${url}:${enabled}`;
  const active = useRef({ scope, onEvent });
  active.current = { scope, onEvent };
  const [connection, setConnection] = useState<CommunicationConnection>('connecting');
  useEffect(() => {
    if (!enabled || !accountID || !url) return;
    const current = () => active.current.scope === scope;
    return openCommunicationSocket({
      url,
      onState: (state) => {
        if (current()) setConnection(state);
      },
      onEvent: (event) => {
        if (!current()) return false;
        // Cookies may change in another tab or while this document is in the
        // browser cache. Never deliver a new identity's events to the old UI.
        if (
          event.type === 'connected' &&
          event.payload?.account_id &&
          event.payload.account_id !== accountID
        ) {
          void qc.invalidateQueries({ queryKey: ['session', authNamespace] });
          return false;
        }
        active.current.onEvent(event);
      },
      validateSession: async (signal) => {
        try {
          const session = await api<Session>(`${authNamespace}/auth/session`, { signal });
          if (signal.aborted || !current()) return false;
          const org = session.organizations.find((item) => item.id === organizationID);
          const valid =
            session.account.id === accountID &&
            !['closing', 'closed'].includes(session.account.lifecycle_status || '') &&
            (!organizationID ||
              (!!org && (!org.lifecycle_status || org.lifecycle_status === 'active'))) &&
            (authNamespace !== '/admin' ||
              ['super_admin', 'operator', 'customer_service'].includes(
                session.account.platform_role,
              ));
          if (!valid) void qc.invalidateQueries({ queryKey: ['session', authNamespace] });
          return valid;
        } catch (error) {
          if (signal.aborted || !current()) return false;
          if (error instanceof ApiError && [401, 403, 409].includes(error.status)) {
            if (error.status === 401) qc.setQueryData(['session', authNamespace], null);
            else void qc.invalidateQueries({ queryKey: ['session', authNamespace] });
            return false;
          }
          throw error;
        }
      },
    });
  }, [scope, url, enabled, accountID, authNamespace, organizationID, qc]);
  return connection;
}

// Coalesce focus/visibility notifications and advance only after the read API
// succeeds. Failed reads remain retryable; old identities cannot update the UI.
export function useConversationRead({
  accountID,
  path,
  seq,
  enabled = true,
  onRead,
}: {
  accountID: string;
  path: string;
  seq?: number;
  enabled?: boolean;
  onRead?: () => void;
}) {
  const scope = `${namespace}:${accountID}:${organization}:${path}`;
  const active = useRef({ scope, onRead });
  active.current = { scope, onRead };
  const positions = useRef(new Map<string, { confirmed: number; pending: Set<number> }>());
  useEffect(() => {
    if (!enabled || !accountID || !seq || seq < 1) return;
    let disposed = false;
    const position = positions.current.get(scope) || { confirmed: 0, pending: new Set<number>() };
    positions.current.set(scope, position);
    const markRead = () => {
      if (
        disposed ||
        active.current.scope !== scope ||
        document.visibilityState !== 'visible' ||
        !document.hasFocus() ||
        seq <= position.confirmed ||
        position.pending.has(seq)
      )
        return;
      position.pending.add(seq);
      void post(path, { seq })
        .then(() => {
          position.confirmed = Math.max(position.confirmed, seq);
          if (!disposed && active.current.scope === scope) active.current.onRead?.();
        })
        .catch(() => undefined)
        .finally(() => position.pending.delete(seq));
    };
    markRead();
    window.addEventListener('focus', markRead);
    document.addEventListener('visibilitychange', markRead);
    return () => {
      disposed = true;
      window.removeEventListener('focus', markRead);
      document.removeEventListener('visibilitychange', markRead);
    };
  }, [scope, accountID, path, seq, enabled]);
}

export async function uploadFile<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  return api<T>(path, { method: 'POST', body: form });
}

function waitForMediaEvent(target: HTMLMediaElement, event: string, timeoutMs = 8000) {
  return new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('视频缩略图生成超时'));
    }, timeoutMs);
    const cleanup = () => {
      window.clearTimeout(timer);
      target.removeEventListener(event, done);
      target.removeEventListener('error', failed);
    };
    const done = () => {
      cleanup();
      resolve();
    };
    const failed = () => {
      cleanup();
      reject(new Error('无法读取视频画面'));
    };
    target.addEventListener(event, done, { once: true });
    target.addEventListener('error', failed, { once: true });
  });
}

async function createVideoThumbnail(file: File): Promise<File | null> {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    file.type !== 'video/mp4'
  ) {
    return null;
  }
  const objectURL = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'metadata';
  video.src = objectURL;
  try {
    video.load();
    await waitForMediaEvent(video, 'loadedmetadata');
    if (!video.videoWidth || !video.videoHeight) return null;

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    if (duration > 0.2) {
      video.currentTime = Math.min(
        Math.max(duration * 0.08, 0.1),
        Math.max(duration - 0.1, 0.1),
        1,
      );
      await waitForMediaEvent(video, 'seeked');
    } else if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      await waitForMediaEvent(video, 'loadeddata');
    }

    const maxWidth = 640;
    const scale = Math.min(1, maxWidth / video.videoWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.82),
    );
    if (!blob) return null;
    return new File([blob], `${file.name}.thumbnail.jpg`, { type: 'image/jpeg' });
  } catch {
    // Thumbnail generation must never block sending the original video. Older
    // messages or unsupported codecs simply render the generic video tile.
    return null;
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(objectURL);
  }
}

export async function uploadCommunicationFile<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  const thumbnail = await createVideoThumbnail(file);
  if (thumbnail) form.append('thumbnail', thumbnail);
  return api<T>(path, { method: 'POST', body: form });
}

export async function privateFileBlob(path: string, options: { direct?: boolean } = {}) {
  const scope = { organization, namespace, csrf };
  const current = () => {
    if (organization !== scope.organization || namespace !== scope.namespace || csrf !== scope.csrf)
      throw new Error('账号或组织已切换，请重新打开文件');
  };
  const headers = new Headers();
  if (organization) headers.set('X-Organization-ID', organization);
  let url = apiURL(path);
  let credentials: RequestCredentials = 'include';
  const direct = options.direct !== false && (await usesDirectStorage());
  current();
  if (direct) {
    const access = await api<{ url: string }>(`${path}${path.includes('?') ? '&' : '?'}access=1`);
    current();
    url = access.url;
    credentials = 'omit';
    for (const key of [...headers.keys()]) headers.delete(key);
  }
  const res = await fetch(url, { headers, credentials });
  current();
  if (!res.ok) throw new Error('文件不可用或无权访问');
  const blob = await res.blob();
  current();
  return blob;
}

export async function downloadPrivateFile(
  path: string,
  name: string,
  options: { direct?: boolean } = {},
) {
  const url = URL.createObjectURL(await privateFileBlob(path, options));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function privateFilePreviewURL(path: string, withOrganization = false) {
  const params = new URLSearchParams();
  if (withOrganization && organization) params.set('organization_id', organization);
  const query = params.toString();
  return apiURL(`${path}${query ? `?${query}` : ''}`);
}
